import { useLatest, useMount, useUpdateEffect } from 'ahooks';
import React from 'react';
import useStateRef from 'react-usestateref';
import { isFunction } from 'lodash-es';
import { shallowEqual } from 'react-redux';
import useContext from '../../../../hooks/useContext';
import { TFieldEvent } from '../../../../types';
import { ApiaComponentContextPrimitive } from '../apiaComponent/ApiaComponentContext';
import useProcessFieldSubmit from './hooks/useProcessFieldSubmit';
import useScriptEvents from './hooks/useScriptEvents';
import useServerEvents from './hooks/useServerEvents';
import fieldsStore from './fieldsStore';
import {
  IFieldActions,
  TFireEvent,
  THookResponse,
  THookResponsePrimitive,
  TInitialValueGetter,
  TSetError,
  TSetValue,
} from './types';
import FieldContext, { TFieldContext, THooksContext } from './FieldContext';
import { formatMessage } from '../../../../utils/util';
import { hasEvents } from './util';
import { ApiaFormContext } from '../page/ApiaForm';
import focusHandler from '../../../../classes/focusController';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import propsStore from '../../../../store/propsStore/propsStore';

const defaultPreScriptEvents: NonNullable<
  IFieldActions['preScriptEvents']
> = () => {
  return true;
};
const defaultPreServerEvents: NonNullable<
  IFieldActions['preServerEvents']
> = () => {
  return true;
};
const defaultPreSynchronize: NonNullable<IFieldActions['preSynchronize']> = ({
  fieldContext: {
    componentContext: { fieldId },
    state: { value, lastValidValue, isSync },
  },
}) => {
  const { readonly, readOnly, formData } = propsStore.getFieldProps(fieldId);
  const isReadonly =
    [String(readonly), String(readOnly), String(formData?.readOnly)].findIndex(
      (current) =>
        current.toLowerCase() === 'true' || current.toLowerCase() === 't',
    ) !== -1;
  if (isReadonly) return 'cancel';
  return value !== lastValidValue || !isSync || 'cancel';
};

export interface IFieldState<ValueType = unknown> {
  firingEvents: number;
  isSync: boolean;
  isValid: boolean;
  lastValidValue: ValueType;
  value: ValueType;
}

/**
 * Este HOC genera un componente de formulario de ejecución de Apia.
 *
 * El comportamiento del componente es genérico y puede ser extendido o
 * modificado a través de modificadores de acción o hooks pasados como segundo
 * parámetro.
 *
 * @param Component El componente que implementa la parte visual.
 * @param actions Es un objeto compuesto de varios métodos que permiten afectar el comportamiento
 * del campo creado, ver fieldsStore/types.ts para conocer  las distintas opciones
 *
 * @returns Un componente de ejecución de Apia.
 *
 * @see IFieldActions
 */

const focusHandlers: Record<string, () => void> = {};

let hasChangedSomeField = false;
let isInitialized = false;
let timeout: ReturnType<typeof setTimeout>;
let hasReachedTaskResult = false;
export function setHasReachedTaskResult(hasReached: boolean) {
  hasReachedTaskResult = hasReached;
}
export function hasChangedAnyField() {
  if (hasReachedTaskResult) return false;
  return (
    (window.CURRENT_STEP !== undefined && window.CURRENT_STEP > 1) ||
    hasChangedSomeField
  );
}
export function setHasChangedAnyField(
  hasChanged: boolean,
  checkInititialization?: boolean,
) {
  if (!isInitialized && checkInititialization) {
    if (timeout !== undefined) clearTimeout(timeout);
    timeout = setTimeout(() => {
      isInitialized = true;
    }, 1000);
    return;
  }
  hasChangedSomeField = hasChanged;
}

import('../../../common/dialogs/modals/ScreenLock')
  .then((current) => {
    current.onScreenLockReleasedFirstTime(() => {
      timeout = setTimeout(() => {
        isInitialized = true;
      }, 1000);
    });
  })
  .catch(console.error);

export default function MakeField(
  Component: React.ForwardRefRenderFunction<unknown, Record<string, unknown>>,
  actions?: IFieldActions,
) {
  const NewComponent: React.FC = React.memo(function MakeFieldComponent() {
    const context = useContext(ApiaComponentContextPrimitive);
    const props = usePropsSelector(
      context.fieldId,
      (cp) => ({
        message: cp.message,
        readOnly: cp.readOnly,
        readonly: cp.readonly,
        disabled: cp.disabled,
        visibilityHidden: cp.visibilityHidden,
        formDataReadOnly: cp.formData?.readOnly,
        value: cp.value,
        required: cp.required,
      }),
      shallowEqual,
    );
    const { formData } = useContext(ApiaFormContext);

    const processFieldSubmit = useProcessFieldSubmit();
    const fireScriptEvent = useScriptEvents();
    const fireServerEvents = useServerEvents();

    const ref = React.useRef(null);
    const isReadonly =
      [
        formData.readOnly,
        formData.readonly,
        props.readOnly,
        props.readonly,
      ].findIndex(
        (element) =>
          String(element).toLowerCase() === 'true' ||
          String(element).toLowerCase() === 't',
      ) !== -1;

    /**
     * ABOUT VALUES
     */
    const getInitialValue: TInitialValueGetter = React.useCallback(
      (tools) => {
        if (actions?.getInitialValue) return actions.getInitialValue(tools);
        if (props.value && !context.field.qry_show_value) {
          return props.value;
        }

        const propsValue = propsStore.getFieldProps(context.fieldId)?.value;
        if (propsValue !== undefined) return propsValue;
        return context.field.value ?? '';
      },
      [
        context.field.qry_show_value,
        context.field.value,
        context.fieldId,
        props.value,
      ],
    );
    const initialValue = React.useMemo(
      () => {
        return getInitialValue({ context });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    const [fieldState, setState, latestState] = useStateRef<IFieldState>({
      firingEvents: 0,
      isValid: true,
      isSync: false,
      lastValidValue: initialValue,
      value: initialValue,
    });

    let setValue: TSetValue = React.useCallback(() => {}, []);

    const getSubmitValue = React.useCallback<
      NonNullable<IFieldActions['getSubmitValue']>
    >((currentValue) => currentValue, []);
    const setError: TSetError = React.useCallback(
      (error: string | null, value?: unknown) => {
        if (value !== undefined) {
          setValue(value, { avoidCleanError: true });
        }
        if (propsStore.getFieldProps(context.fieldId).message !== error) {
          propsStore.updateField(context.fieldId, { message: error });
          setState((currentState) => ({
            ...currentState,
            isValid: !error,
          }));
        }
      },
      [context.fieldId, setState, setValue],
    );

    setValue = React.useCallback(
      (newValue: unknown, configuration) => {
        if (isReadonly && !configuration?.setValueOnReadonly) return;
        let parsedNewValue: unknown;
        setHasChangedAnyField(true, true);
        setState((current) => {
          const actualNewValue = newValue ?? '';
          parsedNewValue = isFunction(actualNewValue)
            ? (actualNewValue(current.value) as unknown)
            : actualNewValue;
          return {
            ...current,
            value: parsedNewValue,
            isSync: false,
            ...(configuration?.setLastValidValue
              ? { lastValidValue: parsedNewValue }
              : undefined),
          };
        });
        propsStore.updateField(
          context.fieldId,
          { value: parsedNewValue },
          { noEmit: true },
        );
        if (!configuration?.avoidCleanError) setError(null);
        if (configuration?.synchronize) void synchronize('onChange', false);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isReadonly, setState],
    );

    useUpdateEffect(() => {
      setState((current) => ({ ...current, isValid: !props.message }));
    }, [props.message]);
    useUpdateEffect(
      function deleteErrorOnValidate() {
        if (fieldState.isValid) setError(null);
      },
      [fieldState.isValid],
    );

    /**
     * FIELD FOCUS HANDLER
     */
    const doFocus = React.useCallback(
      (cb: () => void) => {
        focusHandlers[context.fieldId] = cb;
      },
      [context.fieldId],
    );
    /**
     * ACTIONS
     */
    const focus = React.useCallback(() => {
      if (focusHandlers[context.fieldId]) focusHandlers[context.fieldId]();
      else
        void focusHandler.afterNotifications(
          ref.current as unknown as HTMLElement,
          {
            scrollIntoViewOptions: {
              behavior: 'auto',
              block: 'center',
              inline: 'center',
            },
          },
        );
    }, [context.fieldId]);
    const doValidate: NonNullable<IFieldActions['doValidate']> =
      React.useCallback(
        ({
          fieldContext: {
            state: { value: currentValue },
          },
        }) => {
          return new Promise<boolean>((resolve) => {
            const { required, inputAsText, frmHidden, message } =
              propsStore.getFieldProps(context.fieldId);

            if (message) {
              resolve(false);
              return;
            }

            const hasPassedValidation = (function checkValidity() {
              if (
                required &&
                (!currentValue ||
                  (Array.isArray(currentValue) &&
                    (currentValue.length === 0 ||
                      (currentValue.length === 1 && !currentValue[0])))) &&
                !inputAsText &&
                !frmHidden
              ) {
                setError(formatMessage(window.MSG_REQ_FIELD, { TOK1: '' }));
                return false;
              }
              return true;
            })();

            if (hasPassedValidation) {
              (function wasAlreadyValid() {
                resolve(true);
              })();
            } else {
              resolve(false);
            }
          });
        },
        [context.fieldId, setError],
      );

    const doSynchronize: NonNullable<IFieldActions['doSynchronize']> =
      React.useCallback(
        async (
          {
            fieldContext: {
              componentContext,
              state: { value: currentValue },
            },
            processFieldSubmit: innerPFS,
          },
          additionalProps,
        ) => {
          const value = (
            actions?.getSubmitValue ? actions.getSubmitValue : getSubmitValue
          )(currentValue, componentContext);
          const hasSynchronized = await innerPFS({
            value,
            ...additionalProps,
          });
          if (hasSynchronized)
            setState((current) => ({ ...current, isSync: true }));
          return hasSynchronized;
        },
        [getSubmitValue, setState],
      );

    const doScriptEvents: NonNullable<IFieldActions['doScriptEvents']> =
      React.useCallback(
        async ({ event }) => {
          const hasFiredScriptEvents = await fireScriptEvent(event);
          return hasFiredScriptEvents;
        },
        [fireScriptEvent],
      );

    const doServerEvents: NonNullable<IFieldActions['doServerEvents']> =
      React.useCallback(
        async ({ event }) => {
          const hasFiredServerEvents = await fireServerEvents(event);
          return hasFiredServerEvents;
        },
        [fireServerEvents],
      );

    const actualActions: Omit<
      Required<IFieldActions>,
      'getInitialValue' | 'getSubmitValue' | 'doValidate' | 'willMarkAsLoaded'
    > = React.useMemo(
      () => ({
        ...{
          doScriptEvents,
          doServerEvents,
          doSynchronize,
          preScriptEvents: defaultPreScriptEvents,
          preServerEvents: defaultPreServerEvents,
          preSynchronize: defaultPreSynchronize,
        },
        ...actions,
      }),
      [doScriptEvents, doServerEvents, doSynchronize],
    );
    /**
     * EVENTS CONTROLLER
     *
     * La única cosa que se controla aquí es el orden de las cosas
     * y que todas las etapas se cumplan correctamente, incluidos los hooks.
     *
     * El comportamiento se define en actualActions o a través de los métodos
     * provistos en el parámetro actions
     */
    const hookFail = React.useCallback(
      (message: string) => {
        console.log(message, { state: latestState.current, context });
      },
      [context, latestState],
    );

    const preSynchronize = React.useCallback(
      async (event: TFieldEvent) => {
        const hooksContext: Omit<THooksContext, 'state'> = {
          focus,
          isReadonly,
          setValue,
          setError,
        };

        let preSynchronizeResponse: THookResponsePrimitive = true;

        preSynchronizeResponse = await actualActions.preSynchronize({
          event,
          fieldContext: {
            ...hooksContext,
            componentContext: context,
            state: latestState.current,
          },
        });
        if (preSynchronizeResponse === false) {
          hookFail('Did not pass pre synchronize hook');
          return false;
        }
        if (preSynchronizeResponse !== true) return preSynchronizeResponse;
        return true;
      },
      [
        actualActions,
        context,
        focus,
        hookFail,
        isReadonly,
        latestState,
        setError,
        setValue,
      ],
    );

    const synchronize = React.useCallback(
      async (event: TFieldEvent, shouldPreSynchronize = true) => {
        const hooksContext = {
          componentContext: context,
          focus,
          isReadonly,
          props,
          setValue,
          setError,
        };

        let preSynchronizeResponse: THookResponse | undefined;
        if (shouldPreSynchronize) {
          preSynchronizeResponse = await preSynchronize(event);
          if (preSynchronizeResponse !== true) return preSynchronizeResponse;
        }

        let hasSynchronized: THookResponse | undefined;
        if (!shouldPreSynchronize || preSynchronizeResponse !== 'omit') {
          hasSynchronized = await actualActions.doSynchronize({
            event,
            fieldContext: {
              ...hooksContext,
              state: latestState.current,
            },
            processFieldSubmit,
          });
          if (!hasSynchronized) {
            hookFail('Did not syncrhonize');
            return 'cancel';
          }
          setState((currentState) => ({ ...currentState, isSync: true }));
          return true;
        }
        return false;
      },
      [
        actualActions,
        context,
        focus,
        hookFail,
        isReadonly,
        latestState,
        preSynchronize,
        processFieldSubmit,
        props,
        setError,
        setState,
        setValue,
      ],
    );

    const fireEvent: TFireEvent = React.useCallback(
      function fireEvent(event: TFieldEvent) {
        window.requestAnimationFrame(() => {
          if (event === 'onChange') {
            /**
             * El único estado real del valor del campo es el determinado
             * dentro de MakeField, el store de Redux se actualiza con el único
             * propósito de dar brindar información de forma más sencilla,
             * sobre todo a la hora de utilizar las herramientas de debug de
             * Redux.
             */
            propsStore.updateField(context.fieldId, {
              value: latestState.current.value,
            });
          }

          const hooksContext = {
            componentContext: context,
            focus,
            isReadonly,
            props,
            setValue,
            setError,
          };

          setState((currentState) => ({
            ...currentState,
            firingEvents: currentState.firingEvents + 1,
          }));

          const hasEventsBoolean = hasEvents(context.field);
          async function checkValidityOfEvent() {
            const hasPreSynchronized = await preSynchronize(event);
            if (
              hasPreSynchronized !== true &&
              hasPreSynchronized !== 'emittedWithError'
            )
              return hasPreSynchronized;

            // FIRE SCRIPT EVENTS
            const preScriptsResponse = await actualActions.preScriptEvents({
              event,
              fieldContext: {
                ...hooksContext,
                state: latestState.current,
              },
            });
            const preServerResponse = await actualActions.preServerEvents({
              event,
              fieldContext: {
                ...hooksContext,
                state: latestState.current,
              },
            });

            if (
              (preScriptsResponse === true || preServerResponse === true) &&
              hasEventsBoolean
            ) {
              fieldsStore.markAsBusy(context.fieldId);
            }

            const hasSynchronized = await synchronize(event, false);
            if (hasSynchronized !== true) return hasSynchronized;

            if (
              (hasEventsBoolean || context.field.entityBind) &&
              (preScriptsResponse === true ||
                preScriptsResponse === 'emittedWithError' ||
                preServerResponse === true)
            ) {
              propsStore.updateField(context.fieldId, {
                value: latestState.current.value,
              });

              if (!preScriptsResponse || preScriptsResponse === 'cancel') {
                hookFail('Did not pass pre scripts hook');
                return 'cancel';
              }

              let hasFiredScriptEvents: THookResponse | undefined;
              if (preScriptsResponse !== 'omit') {
                hasFiredScriptEvents = await actualActions.doScriptEvents({
                  event,
                  fieldContext: {
                    ...hooksContext,
                    state: latestState.current,
                  },
                  fireScriptEvents: fireScriptEvent,
                });

                if (!hasFiredScriptEvents) {
                  hookFail('Did not fire scripts correctly');
                  return 'cancel';
                }
              }

              // FIRE SERVER EVENTS
              if (!preServerResponse || preServerResponse === 'cancel') {
                hookFail('Did not pass pre server hook');
                return 'cancel';
              }
              if (preServerResponse !== 'omit') {
                const hasFiredServerEvents = await actualActions.doServerEvents(
                  {
                    event,
                    fieldContext: {
                      ...hooksContext,
                      state: latestState.current,
                    },
                    fireServerEvents,
                  },
                );
                if (!hasFiredServerEvents) {
                  hookFail('Did not fire server events correctly');
                  return 'deleteValue';
                }
              }
            }

            /**
             * En este punto solamente me interesa emittedWithError | true
             */
            return hasPreSynchronized;
          }

          checkValidityOfEvent()
            .then((hasFiredEventCorrectly) => {
              if (hasFiredEventCorrectly === 'cancel') {
                setState((currentState) => ({
                  ...currentState,
                  lastValidValue: currentState.value,
                }));
              } else if (hasFiredEventCorrectly === 'deleteValue') {
                setValue('', {
                  setValueOnReadonly: true,
                  synchronize: true,
                  avoidCleanError: true,
                });
              } else if (hasFiredEventCorrectly) {
                setState((currentState) => ({
                  ...currentState,
                  lastValidValue: currentState.value,
                  isValid: hasFiredEventCorrectly !== 'emittedWithError',
                }));
              } else {
                setState((currentState) => ({
                  ...currentState,
                  value: latestState.current.lastValidValue,
                  isValid: false,
                }));
              }
            })
            .catch(console.error)
            .finally(() => {
              /**
               * Este timeout es necesario ya que la propagación de la acción
               * del botón de confirmar demora más que el evento de servidor,
               * por lo que al comprobar si hay algún evento de servidor en
               * marcha, la respuesta es negativa y con razón, ya que los
               * eventos ya finalizaron.
               *
               * Esto se va a acorregir al hacer el refactor del flujo de
               * confirmación, evitando las demoras generadas por la
               * propagación de eventos.
               */
              setTimeout(() => {
                setState((currentState) => ({
                  ...currentState,
                  firingEvents: currentState.firingEvents - 1,
                }));
                fieldsStore.markAsFree(context.fieldId);
              }, 300);
            });
        });
      },
      [
        context,
        focus,
        isReadonly,
        props,
        setValue,
        setError,
        setState,
        preSynchronize,
        synchronize,
        actualActions,
        latestState,
        hookFail,
        fireScriptEvent,
        fireServerEvents,
      ],
    );

    const markAsLoaded = React.useCallback(() => {
      propsStore.updateField(context.fieldId, { hasFinishedLoading: true });
    }, [context.fieldId]);

    /**
     * Field context
     */
    const fieldContext: TFieldContext = React.useMemo(
      () => ({
        doFocus,
        isReadonly,
        setError,
        setValue,
        state: fieldState,
        fireEvent,
        markAsLoaded,
        props,
      }),
      [
        doFocus,
        fieldState,
        fireEvent,
        isReadonly,
        markAsLoaded,
        props,
        setError,
        setValue,
      ],
    );
    const latestFieldContext = useLatest(fieldContext);

    /**
     * FIELDS STORE REGISTER
     */
    useMount(function registerOnFieldStore() {
      fieldsStore.register({
        doFocus: focus,
        doSynchronize: async (configuration?: {
          additionalProps?: Record<string, unknown>;
          forced?: boolean;
        }) => {
          if (
            (latestState.current.isSync ||
              (context.field as any).isActuallyReadonly ||
              latestState.current.value === latestState.current.value) &&
            !configuration?.forced
          )
            return true;
          const hasSynchronized = await actualActions.doSynchronize(
            {
              event: 'onSubmit',
              fieldContext: {
                ...latestFieldContext.current,
                componentContext: context,
                focus,
                state: latestState.current,
              },
              processFieldSubmit,
            },
            configuration?.additionalProps,
          );
          return hasSynchronized;
        },
        doValidate: async () => {
          const currentProps = propsStore.getFieldProps(context.fieldId);
          if (
            currentProps.disabled ||
            currentProps.visibilityHidden ||
            currentProps.formData.readOnly
          )
            return true;

          const hasValidated = await (actions?.doValidate ?? doValidate)({
            event: 'onSubmit',
            fieldContext: {
              ...latestFieldContext.current,
              componentContext: context,
              focus,
              state: latestState.current,
            },
          });
          if (hasValidated === true) return true;
          return focus;
        },
        fireEvent,
        formData,
        getInitialValue,
        getValue: () => {
          return latestState.current.value;
        },
        id: context.fieldId,
        isBusy: () => {
          return latestState.current.firingEvents !== 0;
        },
        markAsLoaded,
        setError,
        setInitialValue() {
          setValue(getInitialValue({ context }));
        },
        setValue,
      });

      if (
        (context.field.value === undefined && initialValue !== '') ||
        (context.field.value !== undefined &&
          context.field.value !== initialValue)
      ) {
        void synchronize('onChange', false);
      }
      propsStore.updateField(context.fieldId, {
        value: initialValue,
        isSynched: true,
        hasFinishedLoading: !actions?.willMarkAsLoaded,
      });
    });

    return (
      <FieldContext.Provider value={fieldContext}>
        <Component ref={ref} />
      </FieldContext.Provider>
    );
  });
  return NewComponent;
}
